import * as React from "react";
import { observer } from "mobx-react-lite";
import { Typography } from "@material-tailwind/react";

import { Navbar } from "../../Common/Components/Navbar";

type InfoTab = "publisher" | "merchant";

export const InfoPage = observer(() => {
  const [activeTab, setActiveTab] = React.useState<InfoTab>("publisher");

  return (
    <div className="min-h-screen bg-main text-white pb-20">
      <Navbar
        isSearchable={false}
        isBasketIconEnabled={true}
        isAccountIconEnabled={true}
        isInline={true}
      />

      <div className="px-1 sm:px-3 md:px-10 lg:px-16 xl:px-24 mt-8 max-w-4xl mx-auto">
        <Typography variant="h2" className="text-white text-3xl md:text-4xl">
          Newsstand brings you a new, efficient way of stocking print magazines.
        </Typography>

        <div className="inline-block w-full mt-2 mb-6">
          <div className="h-[2px] bg-white animate-expandWidth"></div>
        </div>

        <Typography variant="h4" className="text-white text-xl mb-6">
          The Merchants’ Portal is now live. Independent publishers and retailers,
          please read on.
        </Typography>

        <Typography className="text-lg leading-relaxed mb-6">
          It’s a new way to distribute indie magazines that benefits both sides of the trade. The platform sits
          within the wholesale landscape but tackles two of the industry’s biggest challenges: <b className="font-bold">waste and administration.</b>
          <br />
          <br />
          Think of it as a streamlined conduit. Publishers and retailers retain
          flexibility and control, while <b className="font-bold">payment, packing and postage are handled
            efficiently by Newsstand</b>. Unsold copies become largely a thing of the
          past.
        </Typography>

        <div className="inline-block w-full mt-3 mb-6">
          <div className="h-[1px] bg-white/30"></div>
        </div>

        <Typography className="text-lg leading-relaxed">
          We believe the <b className="font-bold">Merchants’ Portal</b> offers a practical step forward for the
          industry — reducing waste, simplifying administration and improving
          margins for both publishers and retailers.
          <br />
          <br />
          But it’s also a starting point. The benefits are clear, and the finer
          details will continue to evolve with feedback from publishers and
          merchants alike.
          <br />
          <br />
          Newsstand is a trading name of JG Palmer LLP. A family company
          operating in the newspaper and magazine industry since 1898 and based in
          Kent, UK.
          <div className="inline-block w-full mt-9 mb-6">
            <div className="h-[1px] bg-white/30"></div>
          </div>
          <strong className="font-bold">Collaboration might be our middle name. Get in touch.</strong>
          <br />
          Call us on{" "}
          <a className="font-bold underline" href="tel:01227277248">
            01227 277 248
          </a>{" "}
          or email the team at{" "}
          <a
            className="font-bold underline"
            href="mailto:stockists@newsstand.co.uk"
          >
            stockists@newsstand.co.uk
          </a>
          .
          <br />
          <br />
          <b className="font-bold">Visit the portal</b>:{" "}
          <br />
          <a
            className="font-bold underline"
            href="https://stockists.newsstand.co.uk"
            target="_blank"
            rel="noreferrer"
          >
            stockists.newsstand.co.uk
          </a>
          .
        </Typography>

        <div className="flex items-center justify-center w-full mt-8 mb-20">
          <div className="h-[4px] bg-white max-w-[6rem] w-full"></div>
        </div>

        <div className="border-b border-white/30 mb-8">
          <div className="flex gap-6">
            <button
              type="button"
              onClick={() => setActiveTab("publisher")}
              className={`w-full pb-3 text-xl font-semibold transition relative ${activeTab === "publisher"
                ? "text-white"
                : "text-white/50 hover:text-white"
                }`}
            >
              For Publishers
              {activeTab === "publisher" && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-white transition-all duration-300" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("merchant")}
              className={`w-full pb-3 text-xl font-semibold transition relative ${activeTab === "merchant"
                ? "text-white"
                : "text-white/50 hover:text-white"
                }`}
            >
              For Merchant Stockists
              {activeTab === "merchant" && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-white transition-all duration-300" />
              )}
            </button>
          </div>
        </div>

        {activeTab === "publisher" && (
          <div>
            <Typography className="text-lg leading-relaxed mb-6">
              Publishers set their own flexible discounts based on order size.
              They retain their margin and receive <b className="font-bold">monthly payment for firm-sale
                orders</b>, all visible through a simple login. There’s no chasing
              payments, no delay in distribution and no returned copies.
              <br />
              <br />
              Publishers can build relationships directly with stockists while
              avoiding the administrative burden that normally comes with it. The
              portal is already being discovered by buyers worldwide, and
              merchants are notified whenever new issues become available. Over
              time, pre-orders will help guide print runs.
              <br />
              <br />
              Publihers provide <b className="font-bold">one complimentary copy for every ten purchased</b>,
              costed at print cost, helping support the move to firm sale.
            </Typography>

            <Typography variant="h5" className="text-white text-xl mb-4">
              Example
            </Typography>

            <Typography className="text-lg leading-relaxed mb-4">
              Publisher X sells <b className="font-bold">100 copies</b> at a <b className="font-bold">£10 cover price</b> with a <b className="font-bold">£3 print
                cost</b>.
            </Typography>

            <ul className="list-disc pl-6 space-y-3 text-lg mb-6">
              <li>6 merchants buy 10 copies at 50% discount → £300 revenue</li>
              <li>4 merchants buy 5 copies at 40% discount → £120 revenue</li>
              <li>
                10 merchants buy 2 copies at 30% discount → £140 revenue
              </li>
            </ul>

            <Typography className="text-lg leading-relaxed mb-4">
              Total revenue: <b className="font-bold">£560</b>
              <br />
              Retail value: <b className="font-bold">£1,000</b>
              <br />
              <br />
              After the cost of 6 complimentary copies (£18), the publisher
              retains <b className="font-bold">£542</b>, a <b className="font-bold">net margin of 54.2% of cover price</b>, with:
            </Typography>

            <ul className="list-disc pl-6 space-y-2 text-lg">
              <li>Monthly payment</li>
              <li>Full visibility of orders</li>
              <li>Instant distribution</li>
              <li>No returns / huge reduction in waste</li>
              <li>Zero administration</li>
              <li>A new global discovery channel</li>
            </ul>
          </div>
        )}

        {activeTab === "merchant" && (
          <div>
            <Typography className="text-lg leading-relaxed mb-6">
              Merchants can work directly with independent publishers while
              benefiting from some of the <strong className="font-bold">highest discounts in the industry</strong> across{" "}
              <b className="font-bold">170+ indie magazines and counting</b>.
              <br />
              <br />
              The portal shows <b className="font-bold">daily delivery visibility, upcoming issue on-sale dates and available stock levels</b>, with delivery options listed at checkout.
              UK merchants can currently receive <b className="font-bold">up to 28kg tracked in 48 hours
                for around £7</b>, well below typical industry freight costs. International deliveries are extremely
              competitive.
              <br />
              <br />
              Merchants can buy <b className="font-bold">daily, quarterly or anywhere in between</b>,
              depending on their business model.
              <br />
              <br />
              Orders are <b className="font-bold">firm sale</b>, but every <b className="font-bold">10 copies purchased earns a
                complimentary copy</b>, reducing risk and increasing the effective
              discount.
            </Typography>

            <Typography variant="h5" className="text-white text-xl mb-4">
              Example
            </Typography>

            <Typography className="text-lg leading-relaxed mb-4">
              A typical order might include <b className="font-bold">6 new issues plus top-ups on 3 others</b>{" "}
              (all priced at £10).
            </Typography>

            <Typography className="text-lg leading-relaxed mb-4">
              Purchases:
            </Typography>

            <ul className="list-disc pl-6 space-y-3 text-lg mb-6">
              <li>30 copies at 50% discount → £150</li>
              <li>15 copies at 40% discount → £90</li>
              <li>9 copies across smaller orders → £51</li>
            </ul>

            <Typography className="text-lg leading-relaxed">
              Total magazine cost: <b className="font-bold">£291</b>
              <br />
              Delivery: <b className="font-bold">£7</b>
              <br />
              Total spend: <b className="font-bold">£298</b>
              <br />
              <br />
              Retail value of purchased copies: <b className="font-bold">£540</b>
              <br />
              <br />
              Initial purchase discount: <b className="font-bold">44.8%</b>
              <br />
              Including delivery: <b className="font-bold">just over 44%</b>
              <br />
              <br />
              With <b className="font-bold">5 complimentary copies earned</b>, the effective discount rises to{" "}
              <b className="font-bold">around 45–49%</b> if all copies sell.
              <br />
              <br />
              Even with <b className="font-bold">20% unsold stock</b>, the effective discount remains roughly{" "}
              <b className="font-bold">36.9%</b>, with back issues still available to sell.
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
});